import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import "react-responsive-modal/styles.css";
import Style from "./orderBillModal.module.scss";
import { useDispatch, useSelector } from "react-redux";
import "react-image-gallery/styles/css/image-gallery.css";
import Button from "../common/button/button";
import LoadingIndicator from "../reusable/loadingIndicator";
import { RootState } from "../reducers";
import { createOrder } from "../actions/ticket";
import GenericBeppoModal from "./general/genericBeppoModal";
import InputFieldWithLabel from "../common/input/inputFieldWithLabel";
import CountrySelector, { convertLanguageCode }  from "./general/CountrySelector";
import LabelWithHR from "../common/label/labelWithHR";



interface OrderProps {
  onClose: () => void;
  openInfoModal: () => void;
  openInfoModalEmail: () => void;
  openViolatorAddressDeterminedInfoModal: () => void;
  openAlreadyExistMailRequestIn24HoursInfoModal: () => void;
  open: boolean;
  title: string;
}

const OrderBillModal = (props: OrderProps) => {
  const { isLoading } = useSelector((state: RootState) => state.loader);
  const ticket = useSelector((state: RootState) => state.ticket.ticket);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const appLanguageCode = localStorage.getItem("beppoSelectedLanguage") || "deu";
  const apiLanguageCode = convertLanguageCode(appLanguageCode, 'three-letter');

  const {
    register,
    handleSubmit,
    control,
    watch,
    setError,
    formState: { errors },
  } = useForm();

  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const street = watch("street");
  const postalCode = watch("postalCode");
  const city = watch("city");
  const email = watch("email");

  const [sendPost, setSendPost] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>("");



  const onSubmit = async (data: any) => {
    const handleError = (error: string) => {
      setError("firstName", { type: "manual", message: error });
    };

    if (sendEmail && !data.email) {
      setError("email", {
        type: "manual",
        message: t("Order.Email is required"),
      });
      return;
    }

    dispatch(
      createOrder(
        {
          firstName: data.firstName,
          lastName: data.lastName,
          country: selectedCountry,
          street: data.street,
          city: data.city,
          postalCode: data.postalCode,
          email: sendEmail ? data.email : undefined,
          sendPost,
          sendEmail,
          isViolatorAddressDetermined: ticket.violatorAddress?.status === "DETERMINED",
        },
        ticket,
        handleError,
        props.openInfoModal,
        props.openInfoModalEmail,
        props.openViolatorAddressDeterminedInfoModal,
        props.openAlreadyExistMailRequestIn24HoursInfoModal,
        props.onClose
      )
    );
  };

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <GenericBeppoModal
      title={props.title}
      open={props.open}
      onClose={props.onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={Style.formContainer__row}>
          <div className={Style.formContainer__flexOne__margin}>
            <InputFieldWithLabel
              label={t("Order.First name")}
              name="firstName"
              registerName="firstName"
              register={register}
              style={{ marginRight: "5px" }}
              required
              type="text"
            />
          </div>
          <div className={Style.formContainer__flexOne}>
            <InputFieldWithLabel
              label={t("Order.Last name")}
              name="lastName"
              registerName="lastName"
              register={register}
              style={{ marginRight: "5px" }}
              required
              type="text"
            />
          </div>
        </div>

        <div>
          <LabelWithHR label={t("Order.Country")} />
          <Controller
            name="country"
            control={control}
            render={() => (
              <CountrySelector
                selectedCountry={selectedCountry}
                onCountryChange={(country) => setSelectedCountry(country.code)}
                t={t}
                apiLanguageCode={apiLanguageCode}
                className={Style.countryDropdown}
              />
            )}
          />
        </div>

        <InputFieldWithLabel
          label={t("Order.Street and house number")}
          name="street"
          registerName="street"
          register={register}
          style={{ marginRight: "5px" }}
          required
          type="text"
        />

        <InputFieldWithLabel
          label={t("Order.Post code")}
          name="postalCode"
          registerName="postalCode"
          register={register}
          style={{ marginRight: "5px" }}
          required
          type="text"
        />

        <InputFieldWithLabel
          label={t("Order.City")}
          name="city"
          registerName="city"
          register={register}
          style={{ marginRight: "5px" }}
          required
          type="text"
        />

        <div className={Style['checkbox-container']}>
          <label className={Style['checkbox-option']}>
            <input
              type="checkbox"
              checked={sendPost}
              onChange={() => {
                    if (!sendPost) {
                      setSendPost(true);
                      setSendEmail(false);
                    } else {
                      setSendPost(false);
                    }
                }
              }
            />
            {t("Order.Send by post")}
          </label>
          <label className={Style['checkbox-option']}>
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={() => {
                    if (!sendEmail) {
                      setSendEmail(true);
                      setSendPost(false);
                    } else {
                      setSendEmail(false);
                    }
                }
              }
            />
            {t("Order.Send by email")}
          </label>
        </div>

        {sendEmail && (
          <InputFieldWithLabel
            label={t("Order.Email")}
            name="email"
            registerName="email"
            register={register}
            style={{ marginRight: "5px" }}
            required
            type="email"
          />
        )}

        <Button
          text={t("Order.Order")}
          disabled={
            !firstName || !lastName || !street || !postalCode || !city || !selectedCountry ||
            (sendEmail && !email) || (!sendEmail && !sendPost)
          }
          classNameWrapper={Style.buttonDesktop}
        />
      </form>
    </GenericBeppoModal>
  );
};

export default OrderBillModal;
